import React, { useState, useEffect } from 'react';
import { Search, Filter, Eye, Edit, Trash2, Download, ChevronDown, X } from 'lucide-react';
import { getOrders, updateOrder } from '../../services/firestoreService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';

const EnhancedOrdersManagement = () => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [ordersData, customersSnapshot] = await Promise.all([
      getOrders(),
      getDocs(collection(db, COLLECTIONS.CUSTOMERS))
    ]);

    const customersData = [];
    customersSnapshot.forEach((doc) => {
      customersData.push({ id: doc.id, ...doc.data() });
    });

    setOrders(ordersData);
    setCustomers(customersData);
    setLoading(false);
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const result = await updateOrder(orderId, { status: newStatus });
    if (result.success) {
      loadData();
    } else if (result.error === 'not-found') {
      alert('هذا الطلب غير موجود في قاعدة البيانات. سيتم إزالته من القائمة.');
      setOrders(prev => prev.filter(o => o.id !== orderId));
    } else {
      alert(`حدث خطأ أثناء تحديث الطلب: ${result.error}`);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerPhone?.includes(searchTerm);

    const matchesStatus = filterStatus === 'all' || order.status === filterStatus;
    const matchesType = filterType === 'all' || order.type === filterType;

    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-300';
      case 'pending': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'processing': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'مكتمل';
      case 'pending': return 'قيد الانتظار';
      case 'processing': return 'قيد المعالجة';
      case 'cancelled': return 'ملغي';
      default: return status;
    }
  };

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    processing: orders.filter(o => o.status === 'processing').length,
    completed: orders.filter(o => o.status === 'completed').length,
    revenue: orders.filter(o => o.status === 'completed').reduce((sum, o) => sum + (o.total || 0), 0)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة الطلبات</h1>
          <p className="text-gray-600 mt-2">إجمالي الطلبات: {orders.length}</p>
        </div>
        <button className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors">
          <Download className="w-5 h-5" />
          تصدير Excel
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-md p-4">
          <p className="text-sm text-gray-600">الإجمالي</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-yellow-50 rounded-xl shadow-md p-4 border border-yellow-200">
          <p className="text-sm text-yellow-700">قيد الانتظار</p>
          <p className="text-2xl font-bold text-yellow-800">{stats.pending}</p>
        </div>
        <div className="bg-blue-50 rounded-xl shadow-md p-4 border border-blue-200">
          <p className="text-sm text-blue-700">قيد المعالجة</p>
          <p className="text-2xl font-bold text-blue-800">{stats.processing}</p>
        </div>
        <div className="bg-green-50 rounded-xl shadow-md p-4 border border-green-200">
          <p className="text-sm text-green-700">مكتمل</p>
          <p className="text-2xl font-bold text-green-800">{stats.completed}</p>
        </div>
        <div className="bg-purple-50 rounded-xl shadow-md p-4 border border-purple-200">
          <p className="text-sm text-purple-700">الإيرادات</p>
          <p className="text-2xl font-bold text-purple-800">${stats.revenue}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث عن طلب، عميل، أو رقم هاتف..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right appearance-none bg-white"
          >
            <option value="all">جميع الحالات</option>
            <option value="pending">قيد الانتظار</option>
            <option value="processing">قيد المعالجة</option>
            <option value="completed">مكتمل</option>
            <option value="cancelled">ملغي</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right appearance-none bg-white"
          >
            <option value="all">جميع الأنواع</option>
            <option value="maintenance">صيانة</option>
            <option value="accessory">اكسسوارات</option>
            <option value="part">قطع غيار</option>
            <option value="modification">تعديلات</option>
          </select>
        </div>
      </div>

      {filteredOrders.length > 0 ? (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">رقم الطلب</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">العميل</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">النوع</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">المبلغ</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">الحالة</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">التاريخ</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">إجراءات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">#{order.id?.slice(0, 8)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div>
                        <p className="font-medium">{order.customerName || 'غير محدد'}</p>
                        <p className="text-gray-500 text-xs">{order.customerPhone || ''}</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{order.type || 'عام'}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">${order.total || 0}</td>
                    <td className="px-6 py-4">
                      <select
                        value={order.status || 'pending'}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-xs font-medium border cursor-pointer ${getStatusColor(order.status)}`}
                      >
                        <option value="pending">قيد الانتظار</option>
                        <option value="processing">قيد المعالجة</option>
                        <option value="completed">مكتمل</option>
                        <option value="cancelled">ملغي</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {order.createdAt?.toDate?.()?.toLocaleDateString('ar-EG') || 'غير محدد'}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => { setSelectedOrder(order); setShowDetailsModal(true); }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <p>لا توجد طلبات متاحة</p>
        </div>
      )}

      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-8">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">تفاصيل الطلب #{selectedOrder.id?.slice(0, 8)}</h2>
              <button onClick={() => setShowDetailsModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">اسم العميل</p>
                  <p className="font-medium">{selectedOrder.customerName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">رقم الهاتف</p>
                  <p className="font-medium">{selectedOrder.customerPhone}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">البريد الإلكتروني</p>
                  <p className="font-medium">{selectedOrder.customerEmail || 'غير متوفر'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">العنوان</p>
                  <p className="font-medium">{selectedOrder.customerAddress}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-2">تفاصيل المنتج/الخدمة</p>
                {selectedOrder.productDetails && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium">{selectedOrder.productDetails.name}</p>
                    <p className="text-gray-600">{selectedOrder.productDetails.price}</p>
                  </div>
                )}
              </div>

              {selectedOrder.notes && (
                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-2">ملاحظات</p>
                  <p className="bg-gray-50 rounded-lg p-4">{selectedOrder.notes}</p>
                </div>
              )}

              <div className="border-t pt-4 flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">الحالة</p>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium mt-1 ${getStatusColor(selectedOrder.status)}`}>
                    {getStatusText(selectedOrder.status)}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-sm text-gray-600">المبلغ الإجمالي</p>
                  <p className="text-3xl font-bold text-yellow-600">${selectedOrder.total || 0}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedOrdersManagement;
